/**
 * Frosted glass transmission that samples from an SHLightProbe PMREM texture.
 *
 * Instead of Three.js's screen-space transmission pipeline, this samples a
 * prefiltered environment map (PMREM in CubeUV layout) at a roughness-dependent
 * mip level to approximate the view through frosted glass.
 * @module
 */
import type { WebGLRenderTarget } from "three";
import type { LayerSpecificUniformList } from "../../threejs/higharc/LayerSpecificUniformList";
import { safeShaderReplace, type ShaderProgramLike } from "../shaderUtils";

export function frostedGlassUniformsFromPMREM(pmrem: WebGLRenderTarget): LayerSpecificUniformList {
  const cubeSize = pmrem.height / 4;
  return {
    frostedGlassPmrem: pmrem.texture,
    frostedGlassPmremTexelWidth: 1.0 / pmrem.width,
    frostedGlassPmremTexelHeight: 1.0 / pmrem.height,
    frostedGlassPmremMaxMip: Math.floor(Math.log2(cubeSize)),
  };
}

// CubeUV sampling functions adapted from Three.js cube_uv_reflection_fragment,
// prefixed with `fg` to avoid collisions with the engine's own functions.
export const frostedGlassParsFragment = /* glsl */ `
uniform sampler2D frostedGlassPmrem;
uniform float frostedGlassPmremTexelWidth;
uniform float frostedGlassPmremTexelHeight;
uniform float frostedGlassPmremMaxMip;

#define fgCubeUV_minMipLevel 4.0
#define fgCubeUV_minTileSize 16.0

float fgGetFace(vec3 direction) {
  vec3 absDirection = abs(direction);
  float face = -1.0;
  if (absDirection.x > absDirection.z) {
    if (absDirection.x > absDirection.y) {
      face = direction.x > 0.0 ? 3.0 : 0.0;
    } else {
      face = direction.y > 0.0 ? 1.0 : 4.0;
    }
  } else {
    if (absDirection.z > absDirection.y) {
      face = direction.z > 0.0 ? 2.0 : 5.0;
    } else {
      face = direction.y > 0.0 ? 1.0 : 4.0;
    }
  }
  return face;
}

vec2 fgGetUV(vec3 direction, float face) {
  vec2 uv;
  if (face == 0.0) {
    uv = vec2(direction.z, direction.y) / abs(direction.x);
  } else if (face == 1.0) {
    uv = vec2(direction.x, -direction.z) / abs(direction.y);
  } else if (face == 2.0) {
    uv = vec2(direction.x, direction.y) / abs(direction.z);
  } else if (face == 3.0) {
    uv = vec2(-direction.z, direction.y) / abs(direction.x);
  } else if (face == 4.0) {
    uv = vec2(direction.x, direction.z) / abs(direction.y);
  } else {
    uv = vec2(-direction.x, direction.y) / abs(direction.z);
  }
  return 0.5 * (uv + 1.0);
}

vec3 fgBilinearCubeUV(sampler2D pmrem, vec3 direction, float mipInt) {
  float face = fgGetFace(direction);
  float filterInt = max(fgCubeUV_minMipLevel - mipInt, 0.0);
  mipInt = max(mipInt, fgCubeUV_minMipLevel);
  float faceSize = exp2(mipInt);

  highp vec2 uv = fgGetUV(direction, face) * (faceSize - 2.0) + 1.0;

  if (face > 2.0) {
    uv.y += faceSize;
    face -= 3.0;
  }

  uv.x += face * faceSize;
  uv.x += filterInt * 3.0 * fgCubeUV_minTileSize;
  uv.y += 4.0 * (exp2(frostedGlassPmremMaxMip) - faceSize);

  uv.x *= frostedGlassPmremTexelWidth;
  uv.y *= frostedGlassPmremTexelHeight;

  #ifdef texture2DGradEXT
    return texture2DGradEXT(pmrem, uv, vec2(0.0), vec2(0.0)).rgb;
  #else
    return texture2D(pmrem, uv).rgb;
  #endif
}

#define fgCubeUV_r0 1.0
#define fgCubeUV_m0 -2.0
#define fgCubeUV_r1 0.8
#define fgCubeUV_m1 -1.0
#define fgCubeUV_r4 0.4
#define fgCubeUV_m4 2.0
#define fgCubeUV_r5 0.305
#define fgCubeUV_m5 3.0
#define fgCubeUV_r6 0.21
#define fgCubeUV_m6 4.0

float fgRoughnessToMip(float roughness) {
  float mip = 0.0;
  if (roughness >= fgCubeUV_r1) {
    mip = (fgCubeUV_r0 - roughness) * (fgCubeUV_m1 - fgCubeUV_m0) / (fgCubeUV_r0 - fgCubeUV_r1) + fgCubeUV_m0;
  } else if (roughness >= fgCubeUV_r4) {
    mip = (fgCubeUV_r1 - roughness) * (fgCubeUV_m4 - fgCubeUV_m1) / (fgCubeUV_r1 - fgCubeUV_r4) + fgCubeUV_m1;
  } else if (roughness >= fgCubeUV_r5) {
    mip = (fgCubeUV_r4 - roughness) * (fgCubeUV_m5 - fgCubeUV_m4) / (fgCubeUV_r4 - fgCubeUV_r5) + fgCubeUV_m4;
  } else if (roughness >= fgCubeUV_r6) {
    mip = (fgCubeUV_r5 - roughness) * (fgCubeUV_m6 - fgCubeUV_m5) / (fgCubeUV_r5 - fgCubeUV_r6) + fgCubeUV_m5;
  } else {
    mip = -2.0 * log2(1.16 * roughness);
  }
  return mip;
}

vec4 fgTextureCubeUV(sampler2D pmrem, vec3 sampleDir, float roughness) {
  float mip = clamp(fgRoughnessToMip(roughness), fgCubeUV_m0, frostedGlassPmremMaxMip);
  float mipF = fract(mip);
  float mipInt = floor(mip);

  vec3 color0 = fgBilinearCubeUV(pmrem, sampleDir, mipInt);
  if (mipF == 0.0) {
    return vec4(color0, 1.0);
  } else {
    vec3 color1 = fgBilinearCubeUV(pmrem, sampleDir, mipInt + 1.0);
    return vec4(mix(color0, color1, mipF), 1.0);
  }
}
`;

// IOR for glass is 1.5, but the refraction effect for frosted glass is approximate anyway
const IOR = 1.3;
const F0 = ((IOR - 1.0) / (IOR + 1.0)) ** 2;

const frostedGlassFragment = /* glsl */ `
{
  vec3 fgWorldNormal = inverseTransformDirection(normal, viewMatrix);
  vec3 fgWorldViewDir = inverseTransformDirection(geometryViewDir, viewMatrix);

  vec3 fgTransmitDir = refract(-fgWorldViewDir, fgWorldNormal, 1.0 / ${IOR.toFixed(2)});
  // Fall back to straight-through direction on total internal reflection
  if (length(fgTransmitDir) < 0.001) {
    fgTransmitDir = -fgWorldViewDir;
  }

  vec4 fgSample = fgTextureCubeUV(frostedGlassPmrem, fgTransmitDir, roughnessFactor);

  // Schlick Fresnel
  float fgDotNV = saturate(dot(geometryNormal, geometryViewDir));
  float fgFresnel = pow(1.0 - fgDotNV, 5.0);

  float fgF = ${F0.toFixed(4)} + (1.0 - ${F0.toFixed(4)}) * fgFresnel;
  float fgTransmission = 1.0 - fgF;

  totalDiffuse = mix(totalDiffuse, fgSample.rgb * diffuseColor.rgb, fgTransmission);
}
`;

export function includeFrostedGlassTransmission(shader: ShaderProgramLike) {
  shader.fragmentShader = safeShaderReplace(
    shader.fragmentShader,
    "#include <transmission_pars_fragment>",
    frostedGlassParsFragment,
    "frostedGlassTransmission (transmission_pars_fragment)",
  );

  shader.fragmentShader = safeShaderReplace(
    shader.fragmentShader,
    "#include <transmission_fragment>",
    frostedGlassFragment,
    "frostedGlassTransmission (transmission_fragment)",
  );
}
